import classNames from 'classnames';
import { FC, ReactNode } from 'react';

import { BoxProps } from 'app/components/v2/components/Box';
import IconButton, { IconButtonProps } from 'app/components/v2/components/IconButton';
import Pill, { PillProps } from 'app/components/v2/components/Pill';
import Heading4 from 'app/components/v2/components/Text/Heading4';

import CardContent from './CardContent';
import CardFooter, { CardFooterProps } from './CardFooter';
import Box from '../Box';
import Divider from '../Divider';
import Icon, { IconProps } from '../Icon';
import LoadingOverlay from '../LoadingOverlay';

// Local Typings
interface TitleIconProps {
  titleRightIconButtonProps?: never;
  titleRightIconProps?: IconProps;
}
interface TitleIconButtonProps {
  titleRightIconButtonProps?: IconButtonProps;
  titleRightIconProps?: never;
}
type TitleRightIconProps = TitleIconButtonProps | TitleIconProps;
export type CardTitleProps = TitleRightIconProps & {
  hideBorderBottom?: boolean;
  noTitleWrap?: boolean;
  secondaryElement?: ReactNode;
  shouldKeepContentPaddingTop?: boolean;
  subtitle?: ReactNode;
  title: string;
  titleStatusPillProps?: PillProps;
  truncateWidth?: BoxProps['truncateWidth'];
};
export interface CardProps extends BoxProps {
  cardFooterProps?: CardFooterProps;
  cardTitleProps?: CardTitleProps;
  isLoading?: boolean;
  noContentPaddingX?: boolean;
  noContentPaddingY?: boolean;
}

// Local Variables
export const borderColor = 'card_border';

// Component Definition
const Card: FC<CardProps> = ({
  cardFooterProps,
  cardTitleProps,
  children,
  isLoading,
  noContentPaddingX,
  noContentPaddingY,
  ...props
}) => {
  return (
    <Box
      alignItems="center"
      backgroundColor="card_background"
      borderColor={borderColor}
      borderRadius="lg"
      borderWidth={props.borderWidth ?? 1}
      className={classNames(props.className, 'card')}
      display="flex"
      flexDirection="column"
      {...props}
      minHeight={isLoading ? 60 : undefined}
    >
      {cardTitleProps && (
        <>
          <CardContent>
            <Box
              alignItems="flex-start"
              display="flex"
              flexDirection="column"
              flexWrap="wrap"
              fullWidth
              gap={2}
            >
              <Box
                alignItems="center"
                display="flex"
                flexWrap={cardTitleProps.noTitleWrap ? undefined : 'wrap'}
                fullWidth
                gapY={4}
                justifyContent="space-between"
              >
                <Box
                  alignItems="center"
                  display="flex"
                  flexWrap="wrap"
                  gapX={2}
                  gapY={4}
                  maxWidth="100%"
                  minWidth={0}
                >
                  <Heading4
                    fontWeight="bold"
                    truncateWidth={cardTitleProps.truncateWidth}
                  >
                    {cardTitleProps.title}
                  </Heading4>

                  {cardTitleProps.titleRightIconButtonProps && (
                    <IconButton
                      {...cardTitleProps.titleRightIconButtonProps}
                      marginY={-2} // ensures the icon does not increase the height of the card title section
                      ref={cardTitleProps.titleRightIconButtonProps?.ref}
                    />
                  )}

                  {cardTitleProps.titleRightIconProps && <Icon {...cardTitleProps.titleRightIconProps} />}

                  {cardTitleProps.titleStatusPillProps && <Pill {...cardTitleProps.titleStatusPillProps} />}
                </Box>

                {cardTitleProps.secondaryElement && (
                  <Box
                    alignItems="center"
                    display="flex"
                    height={24}
                  >
                    {cardTitleProps.secondaryElement}
                  </Box>
                )}
              </Box>

              {cardTitleProps.subtitle}
            </Box>
          </CardContent>

          {!cardTitleProps.hideBorderBottom && (
            <Divider
              dividerWidth="100%"
              marginY={0}
            />
          )}
        </>
      )}

      <CardContent
        display="block"
        fullHeight
        noPaddingBottom={noContentPaddingY}
        noPaddingTop={!!noContentPaddingY || cardTitleProps?.shouldKeepContentPaddingTop === false}
        noPaddingX={noContentPaddingX}
      >
        {children}
      </CardContent>

      {cardFooterProps && (
        <>
          <Divider
            dividerWidth="100%"
            marginY={0}
          />

          <CardFooter {...cardFooterProps} />
        </>
      )}

      <LoadingOverlay isLoading={!!isLoading} />
    </Box>
  );
};

export default Card;
